import { intl } from "LocaleWrapper";
import { OptionsMeta, Select } from "@cofano/cck";
import { hasPluginPermission } from "imports/Utils";
import { ContractSearch } from "types/index";
import client from "../../../../apollo";
import { ChildPropsFromPagination } from "types/commons";
import { useStoreState } from "ezStore";
import { ContractStatus, CharterSubtype } from "__generated__/enums";
import { GetContractsShortDocument, GetContractsShortQuery } from "__generated__/view/contractsNew/graphql/contract.queries.generated";

type QueriedContract = ChildPropsFromPagination<GetContractsShortQuery, "contracts"> & {
	type?: string | null;
}

const LinkableContractSelect = ({
	getOptionDisabled,
	additionalSearchOptions,
	onChange,
	value,
	disabled,
	showSupplierContractsInstead = false,
}: {
	getOptionDisabled?: (option: QueriedContract) => boolean,
	additionalSearchOptions: ContractSearch,
	onChange: (option: QueriedContract | null) => void,
	value: QueriedContract | null | undefined,
	disabled?: boolean,
	showSupplierContractsInstead?: boolean
}) => {
	const session = useStoreState(state => state.session);
	const filterContractsOnActiveStatuses = hasPluginPermission(session, "filterContractsOnActiveStatuses");

	const onFetchOptions = async (params: string, page: number): Promise<OptionsMeta<QueriedContract>> => {

		const filters: ContractSearch = {
			...additionalSearchOptions,
			contractNumber: params,
			validFrom: !showSupplierContractsInstead ? additionalSearchOptions.validFrom : undefined,
			validUntil: !showSupplierContractsInstead ? additionalSearchOptions.validUntil : undefined,
		};

		try {
			return showSupplierContractsInstead ? await getSupplierContracts(filters, page) : await getLinkableContracts(filters, page, filterContractsOnActiveStatuses);
		} catch (_) {
			return {
				options: [],
				totalResults: 0,
			};
		}
	};

	return (
		<Select
			fetchOptions={onFetchOptions}
			getOptionLabel={option => option.contractNumber ?? ""}
			getOptionDisabled={getOptionDisabled}
			value={value}
			onChange={(option) => onChange(option)}
			groupBy={option => option.type ?? ""}
			popoverHeight={300}
			disabled={disabled}
			renderOption={(props, option) => (
				<li {...props}>
					<div style={{ width: "100%" }}>
						<span>{option.contractNumber}</span>
						{option.description && (
							<>
								<br />
								<small style={{ display: "block", whiteSpace: "wrap", width: "100%" }}>
									{option.description}
								</small>
							</>
						)}
					</div>
				</li>
			)}
			paginated
		/>
	);
};

const getLinkableContracts = async (search: ContractSearch, page: number, onlyShowActive: boolean): Promise<OptionsMeta<QueriedContract>> =>
	client.query({
		query: GetContractsShortDocument,
		variables: {
			search: {
				charterType: search.charterType,
				charterSubtypes: search.charterSubtypes ? search.charterSubtypes : [],
				creditors: search.creditorId ? [ search.creditorId ] : [],
				debtors: search.debtorId ? [ search.debtorId ] : [],
				validFrom: search.validFrom,
				validUntil: search.validUntil,
				isParentOrNoInheritance: true,
				ships: search.shipId ? [ search.shipId ] : [],
				contractNumber: search.contractNumber ? [ search.contractNumber ] : [],
				status: onlyShowActive ? [ ContractStatus.Accepted, ContractStatus.Active, ContractStatus.Finished ] : undefined,
			},
			pagination: {
				page: page - 1,
				size: 10,
			},
		},
	}).then(result => {
		const { linkableParentContracts, linkableContracts } = (result.data.contracts.content ?? []).reduce(
			(acc, contract) => {
				if (contract.isParentContract != null && contract.isParentContract) {
					acc.linkableParentContracts.push(contract);
				} else {
					acc.linkableContracts.push(contract);
				}
				return acc;
			},
			{ linkableParentContracts: [] as QueriedContract[], linkableContracts: [] as QueriedContract[] },
		);

		return {
			options: [
				...linkableParentContracts.map(it => ({
					...it,
					type: intl.formatMessage({ id: "PARENT_CONTRACTS" }),
				})),
				...linkableContracts.map(it => ({
					...it,
					type: intl.formatMessage({ id: "CONTRACTS" }),
				})),
			],
			totalResults: result.data.contracts.totalElements,
		};
	});

const getSupplierContracts = async (search: ContractSearch, page: number): Promise<OptionsMeta<QueriedContract>> =>
	client.query({
		query: GetContractsShortDocument,
		variables: {
			search: {
				charterSubtypes: [ CharterSubtype.Supplier ],
				validFrom: search.validFrom,
				validUntil: search.validUntil,
				ships: search.shipId ? [ search.shipId ] : [],
				contractNumber: search.contractNumber ? [ search.contractNumber ] : [],
			},
			pagination: {
				page: page - 1,
				size: 5,
			},
		},
	}).then(result => ({
		options: [
			...(result.data.contracts.content ?? []).map(it => ({
				...it,
				type: intl.formatMessage({ id: "SUPPLIER_CONTRACT" }),
			})),
		],
		totalResults: result.data.contracts.totalElements,
	}));

export default LinkableContractSelect;
